import { Language, LANGUAGE_COOKIE_KEY, LANGUAGES } from '@workspace/shared-constants';
import Cookie from 'js-cookie';
import { GlobeIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuLabel,
	DropdownMenuTrigger,
} from '@/ui/DropdownMenu';
import { backendApi } from '@/utils/api';
import { AxiosError } from 'axios';
import PopupNotification from '@/domain/core/PopupNotification';

type LanguageSwitcherProps = {
	persistToBackend?: boolean;
	className?: string;
};

const LanguageSwitcher = ({ persistToBackend = true, className }: LanguageSwitcherProps) => {
	const { i18n, t } = useTranslation();

	const changeLang = async (selectedLocale: Language) => {
		if (i18n.language !== selectedLocale) {
			try {
				if (persistToBackend) {
					await backendApi.patch('/users/lang', {
						lang: selectedLocale,
					});
				}

				Cookie.set(LANGUAGE_COOKIE_KEY, selectedLocale);
				await i18n.changeLanguage(selectedLocale);
			} catch (error) {
				console.error('Ошибка обновления языка на бэкенде');

				const axiosError = error as AxiosError<{ message?: string | string[] }>;
				const rawMessage = axiosError.response?.data?.message;
				const message = Array.isArray(rawMessage) ? rawMessage[0] : rawMessage;

				PopupNotification.emitter(
					'error',
					message ? `${t('lang.changeError')}: ${message}` : t('lang.changeError')
				);
				return;
			}
		}
	};

	const currentLanguageName = t(`accessibility.languageSwitcher.${i18n.language}`);

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<button
					aria-label={t('accessibility.languageSwitcher.button', {
						language: currentLanguageName,
					})}
					aria-expanded={false}
					aria-haspopup="menu"
					className={`flex h-[calc(24px*var(--font-scale,1))] min-w-[calc(48px*var(--font-scale,1))] items-center gap-2 ${className ?? ''}`.trim()}
				>
					<div className="text-xs uppercase text-brand-gray-2">{i18n.language}</div>
					<GlobeIcon
						className="size-[calc(24px*var(--font-scale,1))] shrink-0 text-brand-gray-3"
						size={24}
						aria-hidden="true"
					/>
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				role="menu"
				aria-label={t('accessibility.languageSwitcher.menu')}
				align="end"
				sideOffset={8}
				collisionPadding={16}
				className="min-w-[180px]"
			>
				<DropdownMenuLabel id="language-label">{t('lang.change')}</DropdownMenuLabel>
				{LANGUAGES.map((lang) => (
					<DropdownMenuCheckboxItem
						key={lang}
						role="menuitemradio"
						aria-checked={lang === i18n.language}
						aria-labelledby={`language-item-${lang}`}
						checked={lang === i18n.language}
						onCheckedChange={(checked) => {
							if (checked) {
								changeLang(lang);
							}
						}}
					>
						<span id={`language-item-${lang}`}>
							{t(`lang.${lang}`)}
						</span>
					</DropdownMenuCheckboxItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	);
};

export { LanguageSwitcher };
